import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from 'react';
import {
  Container,
  Row,
  Col,
  FormControl,
  InputGroup,
  Button,
  Spinner,
  Card,
  Table,
  Form,
  Badge,
} from 'react-bootstrap';
import Loading from '../shared/loading/Loading';
import { Order, loadDeletedOrders as fetchDeletedOrdersPage } from '../../api/orders';
import {
  deleteOrderServer,
  restoreOrderServer,
} from '../../api/ordersServer';
import {
  Search as FiSearch,
  ArrowLeft as FiArrowLeft,
  Eye as FiEye,
  Trash3 as FiTrash2,
  ArrowCounterclockwise as FiRotateCcw,
} from 'react-bootstrap-icons';
import { useConfirmationDialog } from '../../hooks/useConfirmationDialog';
import { useLoading } from '../../hooks/useLoading';
import OrderView from './OrderView';
import listStyles from '../shared/layout/dataListTable.module.scss';
import { loadConfigurationFactors } from '../../api/configuration';
import type { ProfitPlus2k8OrgConfig } from '../../models/configuration';
import {
  appendProfitPlusManualDeleteNoticeIfNeeded,
  orderWasSyncedWithProfitPlus,
} from '../../utils/profitPlusDeleteConfirmation';

function capitalize(string: string): string {
  return string.charAt(0).toUpperCase() + string.slice(1);
}

function getApprovalStateInfo(state: string) {
  switch (state) {
    case 'pending':
      return { label: 'Pendiente', variant: 'warning' as const };
    case 'approved':
      return { label: 'Aprobado', variant: 'success' as const };
    case 'rejected':
      return { label: 'Rechazado', variant: 'danger' as const };
    default:
      return { label: 'Desconocido', variant: 'secondary' as const };
  }
}

function mapOrderDeleteError(error: any): string {
  const detail = String(error?.response?.data?.detail || error?.message || '').trim();
  const normalized = detail.toLowerCase();
  if (normalized.includes('active returns')) {
    return 'No se puede eliminar la venta porque tiene devoluciones activas. Elimina o resuelve esas devoluciones primero.';
  }
  if (normalized.includes('registered payments')) {
    return 'No se puede eliminar la venta porque su cuenta por cobrar tiene abonos registrados.';
  }
  if (normalized.includes('financial movements')) {
    return 'No se puede eliminar la venta porque su cuenta por cobrar tiene movimientos financieros.';
  }
  if (normalized.includes('cannot be restored')) {
    return 'No se puede restaurar una venta aprobada eliminada porque sus efectos de inventario y CxC ya fueron revertidos.';
  }
  return detail || 'Error desconocido';
}

function getSaleReference(order: Order): string {
  if (order.saleNumber) {
    return `#${order.saleNumber}`;
  }
  if (order.id) {
    return `${order.id.substring(0, 8)}`;
  }
  return '#';
}

interface TrashBinProps {
  device: string;
  addToast: (message: string) => void;
  navStatus: string;
  organizationId: string;
  onBack: () => void;
}

export default function TrashBin({
  device,
  addToast,
  navStatus,
  organizationId,
  onBack,
}: TrashBinProps) {
  const { showConfirmation } = useConfirmationDialog();
  const { showLoading, hideLoading } = useLoading();
  const [orders, setOrders] = useState<Order[]>([]);
  const [loadMore, setLoadMore] = useState(false);
  const [lastDocument, setLastDocument] = useState<any>(null);
  const [loadMorePending, setLoadMorePending] = useState(false);
  const [search, setSearch] = useState('');
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
  const [showOrderView, setShowOrderView] = useState(false);
  const [profitPlus2k8OrgConfig, setProfitPlus2k8OrgConfig] =
    useState<ProfitPlus2k8OrgConfig | null>(null);
  const mountedRef = useRef(true);
  const loadMorePendingRef = useRef(false);

  // Memoized date formatting function
  const formatOrderDate = useCallback((created: any): string => {
    if (!created) return 'N/A';

    try {
      let date;
      // Handle Firestore timestamp format
      if (created && typeof created === 'object' && 'seconds' in created) {
        // Firestore timestamp: { seconds: number, nanoseconds: number }
        date = new Date((created as any).seconds * 1000);
      } else if (created instanceof Date) {
        date = created;
      } else {
        date = new Date(created);
      }

      return isNaN(date.getTime())
        ? 'N/A'
        : date.toLocaleDateString('es-ES', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
          });
    } catch (e) {
      return 'N/A';
    }
  }, []);

  // Load deleted orders (where deleted = true)
  const loadDeletedOrders = useCallback(
    async (searchTerm?: string, lastDoc?: any) => {
      if (loadMorePendingRef.current) return;

      loadMorePendingRef.current = true;
      setLoadMorePending(true);
      try {
        const result = await fetchDeletedOrdersPage(
          organizationId,
          searchTerm,
          lastDoc
        );

        if (mountedRef.current) {
          if (lastDoc) {
            setOrders(prev => [...prev, ...result.orders]);
          } else {
            setOrders(result.orders);
          }
          setLastDocument(result.lastDoc);
          setLoadMore(result.hasMore);
        }
      } catch (e) {
        console.error('Error loading deleted orders:', e);
        if (mountedRef.current) {
          addToast(
            'Error cargando órdenes eliminadas: ' + (e as Error).message
          );
          // Set empty array to prevent further errors
          setOrders([]);
        }
      } finally {
        loadMorePendingRef.current = false;
        if (mountedRef.current) {
          setLoadMorePending(false);
        }
      }
    },
    [organizationId, addToast]
  );

  // Load more orders
  const handleLoadMore = useCallback(() => {
    if (loadMore && !loadMorePending && lastDocument) {
      loadDeletedOrders(search, lastDocument);
    }
  }, [loadMore, loadMorePending, lastDocument, loadDeletedOrders, search]);

  // Handle search
  const handleSearch = useCallback(() => {
    loadDeletedOrders(search);
  }, [loadDeletedOrders, search]);

  // Handle restore order
  const handleRestoreOrder = useCallback(
    async (order: Order) => {
      if (!mountedRef.current) return;

      showLoading('Restaurando Orden', 'overlay');

      try {
        // Use the server API for restore with proper inventory handling
        await restoreOrderServer(organizationId, order.id);

        hideLoading();
        addToast('Orden restaurada exitosamente');

        // Refresh the deleted orders list
        loadDeletedOrders(search);
      } catch (e: any) {
        console.log(e);
        hideLoading();
        addToast(
          `Error restaurando la orden: ${
            mapOrderDeleteError(e)
          }`
        );
      }
    },
    [
      organizationId,
      addToast,
      showLoading,
      hideLoading,
      loadDeletedOrders,
      search,
    ]
  );

  // Handle hard delete order
  const handleHardDeleteOrder = useCallback(
    async (order: Order) => {
      if (!mountedRef.current) return;

      showLoading('Eliminando Permanentemente', 'overlay');

      try {
        await deleteOrderServer(organizationId, order.id, false); // false = hard delete
        hideLoading();
        addToast('Orden eliminada permanentemente');

        // Refresh the deleted orders list
        loadDeletedOrders(search);
      } catch (e) {
        console.log(e);
        hideLoading();
        addToast(`Error eliminando la orden: ${mapOrderDeleteError(e)}`);
      }
    },
    [
      organizationId,
      addToast,
      showLoading,
      hideLoading,
      loadDeletedOrders,
      search,
    ]
  );

  // Handle view order
  const handleViewOrder = useCallback((order: Order) => {
    setSelectedOrder(order);
    setShowOrderView(true);
  }, []);

  // Handle close order view
  const handleCloseOrderView = useCallback(() => {
    setShowOrderView(false);
  }, []);

  const handleOrderViewExited = useCallback(() => {
    setSelectedOrder(null);
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const config = await loadConfigurationFactors(organizationId);
        if (!cancelled) {
          setProfitPlus2k8OrgConfig(config.profitPlus2k8 ?? null);
        }
      } catch {
        if (!cancelled) setProfitPlus2k8OrgConfig(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [organizationId]);

  useEffect(() => {
    mountedRef.current = true;
    loadDeletedOrders();

    return () => {
      mountedRef.current = false;
    };
  }, [loadDeletedOrders]);

  return (
    <Container fluid>
      <Row className="mb-3">
        <Col>
          <div className="d-flex align-items-center gap-3">
            <Button variant="outline-secondary" onClick={onBack}>
              <FiArrowLeft />
            </Button>
            <h4>Papelera de Órdenes</h4>
            <Badge bg="secondary">{orders.length} órdenes eliminadas</Badge>
          </div>
        </Col>
      </Row>

      <Row className="mb-3">
        <Col md={6}>
          <InputGroup>
            <FormControl
              placeholder="Buscar órdenes eliminadas..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleSearch();
                }
              }}
            />
            <Button variant="outline-secondary" onClick={handleSearch}>
              <FiSearch />
            </Button>
          </InputGroup>
        </Col>
      </Row>

      <Card>
        <Card.Body>
          {orders.length === 0 ? (
            <div className="text-center py-4">
              <p className="text-muted">No hay órdenes eliminadas</p>
            </div>
          ) : (
            <div className="table-responsive">
              <Table hover>
                <thead>
                  <tr>
                    <th>Venta</th>
                    <th>Cliente</th>
                    <th>Vendedor</th>
                    <th>Total</th>
                    <th>Estado</th>
                    <th>Fecha</th>
                    <th>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {orders.map(o => {
                    const stateInfo = getApprovalStateInfo(o.approvalState);
                    return (
                      <tr
                        key={o.id}
                        className={listStyles.clickableRow}
                        onClick={() => handleViewOrder(o)}
                      >
                        <td>{getSaleReference(o)}</td>
                        <td>{o.clientName}</td>
                        <td>{o.creatorSalesPerson}</td>
                        <td>${o.total}</td>
                        <td>
                          <Badge bg={stateInfo.variant}>
                            {stateInfo.label}
                          </Badge>
                        </td>
                        <td>{formatOrderDate(o.created)}</td>
                        <td onClick={e => e.stopPropagation()}>
                          <div className="d-flex gap-1">
                            <Button
                              variant="outline-primary"
                              size="sm"
                              onClick={() => handleViewOrder(o)}
                              title="Ver detalles"
                            >
                              <FiEye />
                            </Button>
                            <Button
                              variant="outline-success"
                              size="sm"
                              disabled={o.approvalState === 'approved'}
                              onClick={() =>
                                showConfirmation(
                                  {
                                    title: 'Restaurar Orden',
                                    message:
                                      o.approvalState === 'approved'
                                        ? 'Esta venta fue aprobada antes de eliminarse. Sus efectos de inventario y CxC ya fueron revertidos, por lo que no puede restaurarse de forma segura. Crea una nueva venta si necesitas registrar nuevamente la operación.'
                                        : '¿Estás seguro de que quieres restaurar esta orden?',
                                    confirmText: 'Restaurar',
                                    cancelText: 'Cancelar',
                                  },
                                  () => handleRestoreOrder(o)
                                )
                              }
                              title={
                                o.approvalState === 'approved'
                                  ? 'No se puede restaurar una venta aprobada eliminada'
                                  : 'Restaurar orden'
                              }
                            >
                              <FiRotateCcw />
                            </Button>
                            <Button
                              variant="outline-danger"
                              size="sm"
                              onClick={() => {
                                const basePermanent =
                                  o.approvalState === 'approved'
                                    ? '¿Estás seguro de que quieres eliminar esta orden permanentemente? Esta acción no se puede deshacer. La reversión de inventario y CxC ya debió aplicarse cuando se movió a papelera; esta acción solo elimina el registro histórico visible.'
                                    : '¿Estás seguro de que quieres eliminar esta orden permanentemente? Esta acción no se puede deshacer.';
                                showConfirmation(
                                  {
                                    title: 'Eliminar Permanentemente',
                                    message:
                                      appendProfitPlusManualDeleteNoticeIfNeeded(
                                        basePermanent,
                                        profitPlus2k8OrgConfig,
                                        orderWasSyncedWithProfitPlus(o)
                                      ),
                                    confirmText: 'Eliminar Permanentemente',
                                    cancelText: 'Cancelar',
                                  },
                                  () => handleHardDeleteOrder(o)
                                );
                              }}
                              title="Eliminar permanentemente"
                            >
                              <FiTrash2 />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                  {loadMore === true ? (
                    <tr>
                      <td colSpan={7} className="text-center">
                        <Button
                          variant="outline-primary"
                          onClick={handleLoadMore}
                          disabled={loadMorePending}
                        >
                          {loadMorePending ? (
                            <>
                              <Spinner
                                animation="border"
                                size="sm"
                                className="me-2"
                              />
                              Cargando...
                            </>
                          ) : (
                            'Cargar Más'
                          )}
                        </Button>
                      </td>
                    </tr>
                  ) : null}
                </tbody>
              </Table>
            </div>
          )}
        </Card.Body>
      </Card>

      {selectedOrder && (
        <OrderView
          show={showOrderView}
          onClose={handleCloseOrderView}
          onExited={handleOrderViewExited}
          order={selectedOrder}
        />
      )}
    </Container>
  );
}
